import { DIALOG_DATA } from '@angular/cdk/dialog'

import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnDestroy, OnInit, signal } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import {
  PalantirButtonComponent,
  PalantirDialogComponent,
  PalantirDialogRef,
  PalantirIconComponent,
  PalantirInputComponent,
  PalantirListItemComponent,
  PalantirSkeletonComponent,
  PalantirTextComponent,
} from '@fieldcontrol/palantir'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs'
import { MaintenanceServiceItem } from '../../maintenance-services-tab.type'
import { MaintenanceServicesTabService } from '../../services/maintenance-services-tab.service'

@Component({
  selector: 'app-services-search-dialog',
  templateUrl: './services-search-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    PalantirDialogComponent,
    PalantirButtonComponent,
    PalantirTextComponent,
    PalantirListItemComponent,
    PalantirIconComponent,
    PalantirInputComponent,
    PalantirSkeletonComponent,
    NgScrollbarModule,
    TranslateModule
],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ServicesSearchDialogComponent implements OnInit, OnDestroy {
  private readonly data = inject(DIALOG_DATA)
  private readonly palantirDialogRef = inject(PalantirDialogRef)
  public readonly servicesTabService = inject(MaintenanceServicesTabService)

  private readonly destroy$ = new Subject<void>()

  public services = signal<MaintenanceServiceItem[]>([])
  public selectedItems = signal<MaintenanceServiceItem[]>([])
  public searchControl = new FormControl('')

  public ngOnInit(): void {
    const preSelected: MaintenanceServiceItem[] = this.data?.selectedItems ?? []
    this.selectedItems.set([...preSelected])

    this.searchControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(async () => {
      await this.search()
    })

    void this.search()
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public isSelected(id: string): boolean {
    return this.selectedItems().some((item) => item.id === id)
  }

  public toggleItem(item: MaintenanceServiceItem): void {
    const current = this.selectedItems()
    if (this.isSelected(item.id)) {
      this.selectedItems.set(current.filter((existing) => existing.id !== item.id))
    } else {
      this.selectedItems.set([...current, item])
    }
  }

  public async close(): Promise<void> {
    await this.palantirDialogRef.close()
  }

  public async save(): Promise<void> {
    await this.palantirDialogRef.close({ selectedItems: this.selectedItems() })
  }

  private async search(): Promise<void> {
    this.servicesTabService.filterForm.patchValue({ name_contains: this.searchControl.value })
    this.servicesTabService.offset.set(0)
    this.services.set(await this.servicesTabService.searchPage())
  }
}
